import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, qs, uploadFile } from "@/lib/api";
import { getAutomationPositions, getBillingDetail, getBillingSummary, getUpcomingEvents } from "@/lib/stubs";
import type { AuditEntry, Contact, CustomField, CustomFieldGroup, FileRow, Note, Tag, TagCategory, TagEvent, TimelineItem } from "@/lib/types";

/** Window event the action bar's "Tag" button sends to open the header's tag picker. */
export const OPEN_TAG_PICKER = "crm:open-tag-picker";

export function useContact(id: number) {
  return useQuery({ queryKey: ["contact", id], queryFn: () => api<Contact>(`/api/v1/contacts/${id}`), enabled: id > 0 });
}

/** Timeline areas a person can filter by (kind prefix before the dot). */
export const TIMELINE_AREAS: { key: string; label: string; areas: string[] }[] = [
  { key: "all", label: "Everything", areas: [] },
  { key: "note", label: "Notes", areas: ["note"] },
  { key: "email", label: "Emails", areas: ["email"] },
  { key: "tag", label: "Tags", areas: ["tag"] },
  { key: "form", label: "Forms", areas: ["form"] },
  { key: "money", label: "Orders & payments", areas: ["order", "payment", "refund", "invoice", "subscription"] },
  { key: "automation", label: "Automations", areas: ["automation", "event"] },
  { key: "login", label: "Logins & visits", areas: ["login", "magic_link", "ga4"] },
];

export function useTimeline(id: number, areas: string[] = []) {
  return useInfiniteQuery({
    queryKey: ["timeline", id, areas.join(",")],
    initialPageParam: "",
    queryFn: ({ pageParam }) =>
      api<{ items: TimelineItem[]; next_before?: string }>(`/api/v1/contacts/${id}/timeline` + qs({ before: pageParam, limit: 50, areas: areas.join(",") })),
    getNextPageParam: (last) => last.next_before || undefined,
    enabled: id > 0,
  });
}

export function useTags(includeArchived = false) {
  return useQuery({
    queryKey: ["tags", includeArchived],
    queryFn: () => api<{ tags: Tag[] }>("/api/v1/tags" + qs({ include_archived: includeArchived || undefined })),
    staleTime: 5 * 60_000,
  });
}

export function useTagCategories() {
  return useQuery({ queryKey: ["tag-categories"], queryFn: () => api<{ categories: TagCategory[] }>("/api/v1/tag-categories"), staleTime: 5 * 60_000 });
}

export function useCustomFields(includeArchived = false) {
  return useQuery({
    queryKey: ["custom-fields", includeArchived],
    queryFn: () => api<{ fields: CustomField[] }>("/api/v1/custom-fields" + qs({ include_archived: includeArchived || undefined })),
    staleTime: 5 * 60_000,
  });
}

export function useFieldGroups() {
  return useQuery({ queryKey: ["custom-field-groups"], queryFn: () => api<{ groups: CustomFieldGroup[] }>("/api/v1/custom-field-groups"), staleTime: 5 * 60_000 });
}

export function useNotes(id: number) {
  return useQuery({ queryKey: ["notes", id], queryFn: () => api<{ notes: Note[] }>(`/api/v1/contacts/${id}/notes`), enabled: id > 0 });
}

export function useTagHistory(id: number, enabled = true) {
  return useQuery({ queryKey: ["tag-history", id], queryFn: () => api<{ events: TagEvent[] }>(`/api/v1/contacts/${id}/tag-events?limit=500`), enabled: enabled && id > 0 });
}

export function useContactAudit(id: number, enabled = true) {
  return useQuery({ queryKey: ["contact-audit", id], queryFn: () => api<{ entries: AuditEntry[] }>(`/api/v1/contacts/${id}/audit?limit=300`), enabled: enabled && id > 0 });
}

export function useFiles(id: number, enabled = true) {
  return useQuery({
    queryKey: ["files", id],
    queryFn: () => api<{ files: FileRow[]; storage_ready: boolean }>(`/api/v1/contacts/${id}/files`),
    enabled: enabled && id > 0,
  });
}

export function useBillingSummary(id: number) {
  // Refetch on focus: staff come back from the card form in another tab.
  return useQuery({ queryKey: ["billing-summary", id], queryFn: () => getBillingSummary(id), enabled: id > 0, refetchOnWindowFocus: true });
}
export function useBillingDetail(id: number, enabled = true) {
  return useQuery({ queryKey: ["billing-detail", id], queryFn: () => getBillingDetail(id), enabled: enabled && id > 0, refetchOnWindowFocus: true });
}
export function useAutomationPositions(id: number) {
  return useQuery({ queryKey: ["automation-positions", id], queryFn: () => getAutomationPositions(id), enabled: id > 0 });
}
export function useUpcomingEvents(id: number) {
  return useQuery({ queryKey: ["upcoming-events", id], queryFn: () => getUpcomingEvents(id), enabled: id > 0 });
}

/** Invalidate everything shown on the contact screen after a change. */
function useRefresh(id: number) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["contact", id] });
    void qc.invalidateQueries({ queryKey: ["timeline", id] });
    void qc.invalidateQueries({ queryKey: ["notes", id] });
    void qc.invalidateQueries({ queryKey: ["tag-history", id] });
    void qc.invalidateQueries({ queryKey: ["contact-audit", id] });
  };
}

export function useUpdateContact(id: number) {
  const refresh = useRefresh(id);
  return useMutation({
    mutationFn: (patch: Record<string, unknown>) => api(`/api/v1/contacts/${id}`, { method: "PATCH", json: patch }),
    onSuccess: refresh,
  });
}

export function useTagChange(id: number) {
  const refresh = useRefresh(id);
  return useMutation({
    mutationFn: ({ tagId, remove }: { tagId: number; remove?: boolean }) =>
      api(`/api/v1/contacts/${id}/tags${remove ? "/remove" : ""}`, { json: { tag_ids: [tagId] } }),
    onSuccess: refresh,
  });
}

export function useAddNote(id: number) {
  const refresh = useRefresh(id);
  return useMutation({
    mutationFn: (note: { body: string; kind?: string; title?: string; pinned?: boolean }) => api(`/api/v1/contacts/${id}/notes`, { json: note }),
    onSuccess: refresh,
  });
}

export function useNoteChange(contactId: number) {
  const refresh = useRefresh(contactId);
  return useMutation({
    mutationFn: ({ noteId, patch, remove }: { noteId: number; patch?: { title?: string; body?: string; pinned?: boolean }; remove?: boolean }) =>
      remove ? api(`/api/v1/notes/${noteId}`, { method: "DELETE" }) : api(`/api/v1/notes/${noteId}`, { method: "PATCH", json: patch }),
    onSuccess: refresh,
  });
}

export function useMerge(winnerId: number) {
  const refresh = useRefresh(winnerId);
  return useMutation({
    mutationFn: ({ loserId, reason }: { loserId: number; reason: string }) =>
      api("/api/v1/contacts/merge", { json: { winner_id: winnerId, loser_id: loserId, reason } }),
    onSuccess: refresh,
  });
}

/** Emails, phones and addresses: add / change / remove. */
export function useContactInfo(id: number) {
  const refresh = useRefresh(id);
  const base = `/api/v1/contacts/${id}`;
  const opts = { onSuccess: refresh };
  return {
    addEmail: useMutation({ mutationFn: (v: { email: string; label?: string }) => api(`${base}/emails`, { json: v }), ...opts }),
    updateEmail: useMutation({
      mutationFn: ({ itemId, ...v }: { itemId: number; label?: string; make_primary?: boolean }) => api(`${base}/emails/${itemId}`, { method: "PATCH", json: v }),
      ...opts,
    }),
    removeEmail: useMutation({ mutationFn: (itemId: number) => api(`${base}/emails/${itemId}`, { method: "DELETE" }), ...opts }),
    addPhone: useMutation({ mutationFn: (v: { number: string; type?: string; extension?: string }) => api(`${base}/phones`, { json: v }), ...opts }),
    updatePhone: useMutation({
      mutationFn: ({ itemId, ...v }: { itemId: number; number?: string; type?: string; extension?: string; make_primary?: boolean }) =>
        api(`${base}/phones/${itemId}`, { method: "PATCH", json: v }),
      ...opts,
    }),
    removePhone: useMutation({ mutationFn: (itemId: number) => api(`${base}/phones/${itemId}`, { method: "DELETE" }), ...opts }),
    setAddress: useMutation({
      mutationFn: ({ kind, ...v }: { kind: string; line1?: string; line2?: string; city?: string; region?: string; postal_code?: string; country_code?: string }) =>
        api(`${base}/addresses/${kind}`, { method: "PUT", json: v }),
      ...opts,
    }),
  };
}

export function useUploadFile(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadFile<FileRow>(`/api/v1/contacts/${id}/files`, file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["files", id] });
      void qc.invalidateQueries({ queryKey: ["timeline", id] });
    },
  });
}
